import { useEffect } from 'react';
import { Button, Link } from 'carbon-components-react';
import { ArrowRight16, Chat20, Subtract32 } from '@carbon/icons-react';
import './NoteDrawer.scss';
import awsimage from '../../public/aws.png';
import ibmimage from '../../public/ibm.png';
import { segmentsTracking } from 'src/common/lib/tracker';
import messages from '../../locales/messages';
import { FormattedMessage } from 'react-intl';
import { ProductInterface } from '../Omnichannel';

export interface NoteDrawer {
  omniChannel: boolean;
  setOmniChannel: React.Dispatch<React.SetStateAction<boolean>>;
  id: string;
  environment: string;
  resultValue?: string;
  ctaValue?: string;
  action?: string;
  field?: string;
  editionId: string;
  offering: string;
  ibmMarketPlaceHref: string;
  headerTitle: string;
  product: ProductInterface;
}

export default function NoteDrawer({
  id,
  omniChannel,
  setOmniChannel,
  action = '',
  field = '',
  editionId,
  offering,
  ibmMarketPlaceHref,
  headerTitle,
  product,
}: NoteDrawer) {
  useEffect(() => {
    if (omniChannel && headerTitle !== '') {
      segmentsTracking('CTA Clicked', {
        productTitle: product.productName,
        pageTitle: product.pageTitle,
        pageUrl: product.pageUrl,
        object: product.object,
        resultValue: 'CTA clicked',
        CTA: 'ominichannel slider opened',
        location: product.location,
        subscriptionId: editionId,
        productPlanName: offering,
        action: action,
        field: field,
      });
    }
  }, [omniChannel, headerTitle, product]);

  const closeDrawer = () => {
    setOmniChannel(false);
    segmentsTracking('CTA Clicked', {
      productTitle: product.productName,
      pageTitle: product.pageTitle,
      pageUrl: product.pageUrl,
      object: product.object,
      resultValue: 'CTA clicked',
      CTA: 'ominichannel slider closed',
      location: product.location,
      action: action,
      field: field,
    });
  };

  return (
    <div id={id} aria-label="Omnichannel Drawer" aria-hidden={!omniChannel}>
      <div
        className={`widget-drawer-backdrop ${
          omniChannel ? 'opened' : 'closed'
        } widget-drawer-transition`}
        onClick={closeDrawer}
      ></div>
      <div
        className={`widget-drawer ${
          omniChannel ? 'opened' : 'closed'
        } widget-drawer-transition`}
      >
        <div className="widget-drawer-inner-container">
          <div className="bx--row">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
              <div className="widget-drawer-close-container">
                <Button
                  className="widget-drawer-close-btn"
                  kind="ghost"
                  renderIcon={Subtract32}
                  onClick={closeDrawer}
                ></Button>
              </div>
            </div>
          </div>
          <div className="widget-drawer-notes-container">
            <div className="bx--row">
              <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                <p className="slide_text widgetname">{product.productName}</p>
              </div>
              <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                <p className="page_sub_heading">
                  <FormattedMessage {...messages.buy_now_yours} />
                </p>
              </div>
              <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4 mt-02">
                <p className="page_body">
                  {product.productName} SaaS {offering}{' '}
                  <FormattedMessage {...messages.main_header} />
                </p>
              </div>
              {product.isIbmMarket ? (
                <div className="cta-section">
                  <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                    {/* For future notifications */}
                  </div>
                  <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                    <img className="img_logo" src={ibmimage} />
                  </div>
                  <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4 cta">
                    <p className="page_body cta-text">
                      <FormattedMessage {...messages.ibm_desc_buy} />{' '}
                      {product.productName}{' '}
                      <FormattedMessage {...messages.ibm_desc} />
                    </p>
                    <Button
                      className="omnichannel-panel__bluehighlight"
                      kind="tertiary"
                      renderIcon={ArrowRight16}
                      id="request_btn"
                      onClick={() => {
                        segmentsTracking('CTA Clicked', {
                          productTitle: product.productName,
                          pageTitle: product.pageTitle,
                          pageUrl: product.pageUrl,
                          object: product.object,
                          resultValue: 'CTA clicked - Omnichannel',
                          CTA: 'ibm marketplace',
                          location: product.location,
                          action: action,
                          field: field,
                          subscriptionId: editionId,
                          productPlanName: offering,
                        });
                        setTimeout(async () => {
                          window.open(`${ibmMarketPlaceHref}`, '_blank');
                        }, 250);
                      }}
                    >
                      <FormattedMessage {...messages.buy_now_ibm} />
                    </Button>
                  </div>
                </div>
              ) : (
                <></>
              )}
              {product.awsUrl ? (
                <div className="cta-section">
                  <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                    <img className="img_logo" src={awsimage} />
                  </div>
                  <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4 cta">
                    <p className="page_body cta-text">
                      <FormattedMessage {...messages.aws_desc} />
                    </p>
                    <Button
                      className="omnichannel__bluehighlight"
                      kind="tertiary"
                      renderIcon={ArrowRight16}
                      id="request_btn"
                      onClick={() => {
                        segmentsTracking('CTA Clicked', {
                          productTitle: product.productName,
                          pageTitle: product.pageTitle,
                          pageUrl: product.pageUrl,
                          object: product.object,
                          resultValue: 'CTA clicked - Omnichannel',
                          CTA: 'aws marketplace',
                          location: product.location,
                          action: action,
                          field: field,
                          productPlanName: offering,
                        });
                        setTimeout(async () => {
                          window.open(product.awsUrl, '_blank');
                        }, 250);
                      }}
                    >
                      <FormattedMessage {...messages.buy_now_aws} />
                    </Button>
                  </div>
                </div>
              ) : (
                <></>
              )}
              {product.azureUrl ? (
                <div className="cta-section">
                  <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4">
                    <img className="img_logo" src="" alt="azure-image" />
                  </div>
                  <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4 cta">
                    <p className="page_body cta-text">
                      <FormattedMessage {...messages.azure_desc} />
                    </p>
                    <Button
                      className="omnichannel__bluehighlight"
                      kind="tertiary"
                      renderIcon={ArrowRight16}
                      id="request_btn"
                      onClick={() => {
                        segmentsTracking('CTA Clicked', {
                          productTitle: product.productName,
                          pageTitle: product.pageTitle,
                          pageUrl: product.pageUrl,
                          object: product.object,
                          resultValue: 'CTA clicked - Omnichannel',
                          CTA: 'azure marketplace',
                          location: product.location,
                          action: action,
                          field: field,
                          productPlanName: offering,
                        });
                        setTimeout(async () => {
                          window.open(product.awsUrl, '_blank');
                        }, 250);
                      }}
                    >
                      <FormattedMessage {...messages.buy_now_azure} />
                    </Button>
                  </div>
                </div>
              ) : (
                <></>
              )}
              {product.isConsultSeller ? (
                <div className="bx--col-lg-16 bx--col-md-8 bx--col-sm-4 mt-02">
                  <Link
                    href="javascript:void 0"
                    onClick={() => {
                      setOmniChannel(false);
                      segmentsTracking('CTA Clicked', {
                        productTitle: product.productName,
                        pageTitle: product.pageTitle,
                        pageUrl: product.pageUrl,
                        object: product.object,
                        resultValue: 'CTA clicked - Omnichannel',
                        CTA: 'consult with a seller via omnichannel',
                        location: product.location,
                        action: action,
                        field: field,
                        productPlanName: offering,
                      });
                    }}
                    data-ibm-contact="contact-link"
                    aria-label="Contact"
                    className="cta-consult-seller"
                    renderIcon={() => <Chat20 />}
                  >
                    <FormattedMessage {...messages.consult_a_seller} />
                  </Link>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
